import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  BookOpen, Search, ChevronRight, ArrowLeft, BookMarked,
  Hash, User, Star, ChevronLeft, Loader2, BookText, X
} from 'lucide-react';
import {
  getHadithBooks,
  getHadithChapters,
  getHadithsByBook,
  searchHadiths,
} from '../lib/api';

const GRADE_COLORS = {
  'Sahih': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Hasan': 'bg-blue-100 text-blue-700 border-blue-200',
  "Da'eef": 'bg-amber-100 text-amber-700 border-amber-200',
  "Da`eef": 'bg-amber-100 text-amber-700 border-amber-200',
};

function gradeStyle(grade) {
  return GRADE_COLORS[grade] || 'bg-stone-100 text-stone-600 border-stone-200';
}

function HadithCard({ hadith }) {
  const [showArabic, setShowArabic] = useState(true);

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between px-5 py-3 bg-stone-50 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-sm font-semibold text-stone-500">
            <Hash className="w-3.5 h-3.5" />
            {hadith.hadithNumber}
          </span>
          {hadith.book?.bookName && (
            <span className="text-xs text-stone-400 hidden sm:inline">· {hadith.book.bookName}</span>
          )}
          {hadith.chapter?.chapterEnglish && (
            <span className="text-xs text-stone-400 hidden sm:inline">· {hadith.chapter.chapterEnglish}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hadith.status && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${gradeStyle(hadith.status)}`}>
              {hadith.status}
            </span>
          )}
          <button
            onClick={() => setShowArabic(v => !v)}
            className="text-xs px-2 py-0.5 rounded-full bg-stone-200 hover:bg-stone-300 text-stone-600 transition-colors"
          >
            {showArabic ? 'EN' : 'ع'}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {hadith.headingEnglish && (
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">
            {hadith.headingEnglish}
          </p>
        )}

        {showArabic && hadith.hadithArabic && (
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <p
              className="text-xl leading-loose text-right text-stone-800"
              dir="rtl"
              style={{ fontFamily: "'Scheherazade New', 'Amiri', serif" }}
            >
              {hadith.hadithArabic}
            </p>
          </div>
        )}

        {hadith.englishNarrator && (
          <div className="flex items-start gap-2">
            <User className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-teal-700 font-medium leading-relaxed">
              {hadith.englishNarrator}
            </p>
          </div>
        )}

        {hadith.hadithEnglish && (
          <p className="text-base text-stone-700 leading-relaxed">
            {hadith.hadithEnglish}
          </p>
        )}
      </div>
    </div>
  );
}

function Pagination({ currentPage, lastPage, onPageChange }) {
  if (lastPage <= 1) return null;

  const pages = [];
  const delta = 2;
  for (let i = Math.max(1, currentPage - delta); i <= Math.min(lastPage, currentPage + delta); i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-center gap-1 pt-6">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg hover:bg-stone-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4 text-stone-600" />
      </button>
      {currentPage > delta + 1 && (
        <>
          <button onClick={() => onPageChange(1)} className="px-3 py-1.5 rounded-lg text-sm hover:bg-stone-100 text-stone-600">1</button>
          {currentPage > delta + 2 && <span className="text-stone-400 text-sm px-1">…</span>}
        </>
      )}
      {pages.map(p => (
        <button
          key={p}
          onClick={() => onPageChange(p)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            p === currentPage
              ? 'bg-teal-600 text-white'
              : 'hover:bg-stone-100 text-stone-600'
          }`}
        >
          {p}
        </button>
      ))}
      {currentPage < lastPage - delta && (
        <>
          {currentPage < lastPage - delta - 1 && <span className="text-stone-400 text-sm px-1">…</span>}
          <button onClick={() => onPageChange(lastPage)} className="px-3 py-1.5 rounded-lg text-sm hover:bg-stone-100 text-stone-600">{lastPage}</button>
        </>
      )}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === lastPage}
        className="p-2 rounded-lg hover:bg-stone-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRight className="w-4 h-4 text-stone-600" />
      </button>
    </div>
  );
}

export default function Hadith({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [view, setView] = useState('books');
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [hadiths, setHadiths] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalHadiths, setTotalHadiths] = useState(0);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchLastPage, setSearchLastPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [activeSearch, setActiveSearch] = useState('');

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const data = await getHadithBooks();
      setBooks(data);
    } catch (error) {
      console.error('Error fetching books:', error);
      toast.error(lang === 'hr' ? 'Greška pri učitavanju knjiga' : 'Error loading books');
    } finally {
      setLoading(false);
    }
  };

  const selectBook = async (book) => {
    setSelectedBook(book);
    setSelectedChapter(null);
    setHadiths([]);
    setView('chapters');
    setLoading(true);
    try {
      const data = await getHadithChapters(book.bookSlug);
      setChapters(data);
    } catch (error) {
      console.error('Error fetching chapters:', error);
      toast.error(lang === 'hr' ? 'Greška pri učitavanju poglavlja' : 'Error loading chapters');
    } finally {
      setLoading(false);
    }
  };

  const selectChapter = useCallback(async (chapter, page = 1) => {
    setSelectedChapter(chapter);
    setView('hadiths');
    setCurrentPage(page);
    setLoading(true);
    try {
      const result = await getHadithsByBook(selectedBook.bookSlug, chapter.bookNumber, page, 20);
      setHadiths(result.data);
      setCurrentPage(result.currentPage);
      setLastPage(result.lastPage);
      setTotalHadiths(result.total);
    } catch (error) {
      console.error('Error fetching hadiths:', error);
      toast.error(lang === 'hr' ? 'Greška pri učitavanju hadisa' : 'Error loading hadiths');
    } finally {
      setLoading(false);
    }
  }, [selectedBook, lang]);

  const handlePageChange = (page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    selectChapter(selectedChapter, page);
  };

  const performSearch = async (query, page = 1) => {
    if (!query.trim()) return;
    setSearching(true);
    setActiveSearch(query);
    setView('search');
    try {
      const result = await searchHadiths(query, page);
      setSearchResults(result.data);
      setSearchPage(result.currentPage);
      setSearchLastPage(result.lastPage);
      setSearchTotal(result.total);
    } catch (error) {
      console.error('Error searching hadiths:', error);
      toast.error(lang === 'hr' ? 'Greška pri pretrazi' : 'Error searching');
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) performSearch(searchQuery);
  };

  const handleSearchPageChange = (page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    performSearch(activeSearch, page);
  };

  const goBack = () => {
    if (view === 'hadiths') {
      setView('chapters');
      setSelectedChapter(null);
      setHadiths([]);
    } else if (view === 'chapters') {
      setView('books');
      setSelectedBook(null);
      setChapters([]);
    } else if (view === 'search') {
      setView('books');
      setSearchResults([]);
      setActiveSearch('');
    }
  };

  const breadcrumb = () => {
    const items = [{ label: lang === 'hr' ? 'Kolekcije' : 'Collections', onClick: () => { setView('books'); setSelectedBook(null); setChapters([]); setSelectedChapter(null); setHadiths([]); } }];
    if (selectedBook && (view === 'chapters' || view === 'hadiths')) {
      items.push({ label: selectedBook.title, onClick: () => { setView('chapters'); setSelectedChapter(null); setHadiths([]); } });
    }
    if (selectedChapter && view === 'hadiths') {
      items.push({ label: selectedChapter.book, onClick: null });
    }
    if (view === 'search') {
      items.push({ label: `"${activeSearch}"`, onClick: null });
    }
    return items;
  };

  return (
    <Layout user={user}>
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-teal-50/30">
        <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center shadow-sm">
                <BookMarked className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-stone-900">
                  {lang === 'hr' ? 'Hadisi' : 'Hadith Collections'}
                </h1>
                <p className="text-sm text-stone-500">
                  {lang === 'hr' ? 'Pretraži i proučavaj hadise' : 'Browse and study prophetic traditions'}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSearchSubmit} className="mb-6">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={lang === 'hr' ? 'Pretraži hadise na engleskom...' : 'Search hadiths in English...'}
                  className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white shadow-sm"
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={searching || !searchQuery.trim()}
                className="px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {lang === 'hr' ? 'Pretraži' : 'Search'}
              </button>
            </div>
          </form>

          {view !== 'books' && (
            <div className="flex items-center gap-1 mb-5 flex-wrap">
              {breadcrumb().map((item, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-stone-400" />}
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      className="text-sm text-teal-600 hover:text-teal-800 hover:underline transition-colors"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className="text-sm text-stone-600 font-medium truncate max-w-[200px]">{item.label}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-3" />
                <p className="text-stone-500 text-sm">{lang === 'hr' ? 'Učitavanje...' : 'Loading...'}</p>
              </div>
            </div>
          ) : view === 'books' ? (
            <div>
              <h2 className="text-lg font-semibold text-stone-700 mb-4">
                {lang === 'hr' ? 'Odaberi Kolekciju' : 'Select a Collection'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {books.map((book) => (
                  <button
                    key={book.bookSlug}
                    onClick={() => selectBook(book)}
                    className="group p-5 bg-white rounded-2xl border border-stone-200 hover:border-teal-400 hover:shadow-md transition-all text-left"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-9 h-9 rounded-lg bg-teal-50 group-hover:bg-teal-100 flex items-center justify-center transition-colors">
                        <BookOpen className="w-4 h-4 text-teal-600" />
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-teal-500 transition-colors mt-1" />
                    </div>
                    <h3 className="font-bold text-stone-800 group-hover:text-teal-700 transition-colors mb-1 leading-snug">
                      {book.title}
                    </h3>
                    {book.writerName && (
                      <p className="text-xs text-stone-500 mb-2 line-clamp-1">{book.writerName}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-stone-400 flex items-center gap-1">
                        <BookText className="w-3 h-3" />
                        {book.totalHadith?.toLocaleString()} {lang === 'hr' ? 'hadisa' : 'hadiths'}
                      </span>
                      <span className="text-xs text-stone-400">
                        {book.chaptersCount} {lang === 'hr' ? 'poglavlja' : 'chapters'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : view === 'chapters' ? (
            <div>
              <div className="mb-6 p-5 bg-white rounded-2xl border border-stone-200 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-stone-900">{selectedBook?.title}</h2>
                    {selectedBook?.writerName && (
                      <p className="text-sm text-stone-500 mt-0.5">{selectedBook.writerName}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-teal-600">{selectedBook?.totalHadith?.toLocaleString()}</p>
                    <p className="text-xs text-stone-400">{lang === 'hr' ? 'hadisa' : 'hadiths'}</p>
                  </div>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3">
                {lang === 'hr' ? 'Poglavlja' : 'Chapters'} ({chapters.length})
              </h3>
              <div className="space-y-2">
                {chapters.map((chapter, idx) => (
                  <button
                    key={chapter.id || idx}
                    onClick={() => selectChapter(chapter)}
                    className="w-full group flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200 hover:border-teal-400 hover:shadow-sm transition-all text-left"
                  >
                    <span className="w-8 h-8 rounded-lg bg-stone-100 group-hover:bg-teal-50 flex items-center justify-center text-xs font-bold text-stone-500 group-hover:text-teal-600 transition-colors flex-shrink-0">
                      {chapter.bookNumber}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 group-hover:text-teal-700 transition-colors">
                        {chapter.book}
                      </p>
                      {chapter.arabicBook && (
                        <p className="text-sm text-stone-500 mt-0.5 font-arabic truncate" dir="rtl">
                          {chapter.arabicBook}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-teal-500 transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ) : view === 'hadiths' ? (
            <div>
              <div className="mb-5 p-4 bg-white rounded-2xl border border-stone-200 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide font-semibold">{selectedBook?.title}</p>
                  <h2 className="text-lg font-bold text-stone-900">{selectedChapter?.book}</h2>
                  {selectedChapter?.arabicBook && (
                    <p className="text-sm text-stone-500 font-arabic" dir="rtl">{selectedChapter.arabicBook}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-teal-600">{totalHadiths}</p>
                  <p className="text-xs text-stone-400">{lang === 'hr' ? 'hadisa' : 'hadiths'}</p>
                </div>
              </div>

              {hadiths.length === 0 ? (
                <div className="text-center py-16 text-stone-400">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>{lang === 'hr' ? 'Nema hadisa u ovom poglavlju' : 'No hadiths in this chapter'}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {hadiths.map((hadith, idx) => (
                      <HadithCard key={hadith.id || idx} hadith={hadith} />
                    ))}
                  </div>
                  <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={handlePageChange} />
                </>
              )}
            </div>
          ) : view === 'search' ? (
            <div>
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-stone-900">
                    {lang === 'hr' ? 'Rezultati pretrage' : 'Search Results'}
                  </h2>
                  <p className="text-sm text-stone-500">
                    {searching
                      ? (lang === 'hr' ? 'Pretraživanje...' : 'Searching...')
                      : `${searchTotal} ${lang === 'hr' ? 'rezultata za' : 'results for'} "${activeSearch}"`
                    }
                  </p>
                </div>
                <button
                  onClick={goBack}
                  className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {lang === 'hr' ? 'Nazad' : 'Back'}
                </button>
              </div>

              {searching ? (
                <div className="flex items-center justify-center py-24">
                  <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-16 text-stone-400">
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>{lang === 'hr' ? 'Nema rezultata' : 'No results found'}</p>
                  <p className="text-sm mt-1">{lang === 'hr' ? 'Pokušaj s drugim pojmom' : 'Try a different search term'}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {searchResults.map((hadith, idx) => (
                      <HadithCard key={hadith.id || idx} hadith={hadith} />
                    ))}
                  </div>
                  <Pagination currentPage={searchPage} lastPage={searchLastPage} onPageChange={handleSearchPageChange} />
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
